/**
 * /field/wt/jobs/:id/reading — the hand's "Enter reading" (GET / POST
 * /well-testing/field/jobs/:id/readings, fieldiq-api#1248). SNAKE_CASE.
 *
 * PREFILLED FROM THE LAST READING: each value as it was typed, in the unit it
 * was typed in, and each tank gauge. A hand changes what moved and saves.
 *
 * THE COUNTDOWN is the API's `next_due_at` (the last reading plus the job's
 * interval) against the phone's clock; nothing here decides the interval.
 *
 * UNITS: every field has a picker defaulting to the tenant's unit (or the
 * unit the last reading was typed in). The value goes to the API as typed.
 *
 * CREW ONLY: 403 not_on_crew reads as a sentence. A job that takes no
 * readings (draft, completed, cancelled) says so instead of the form. A save
 * the API refuses is shown in plain words where the button was.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSaveWtFieldReading, useWtFieldJob, useWtFieldReadings, useWtMeta } from '../../../hooks/useWellTesting';
import { formatInstant } from '../../../lib/calendar-date';
import { wtDueText, wtMinutesUntil, wtRefusal, WT_READING_REFUSALS } from '../../../lib/well-testing';
import type { WtFieldReadings } from '../../../types/well-testing';
import { PtFieldCard, PtFieldPage, PtFieldQueryState, PtRefusal } from '../../pressure-testing/field/PtFieldBits';
import { ReadingForm, type ReadingFormField, type ReadingFormInitial, type ReadingFormTank } from '../ReadingForm';
import { wtNotOnCrewMessage } from './WtFieldJobScreen';

/** The phone's snake_case reading fields in the form's shape. */
export const fieldFormFields = (data: WtFieldReadings | null): ReadingFormField[] =>
  (data?.fields ?? []).map((f) => ({ key: f.key, label: f.label, kind: f.kind, group: f.group, defaultUnit: f.default_unit }));

/** The last reading as the form's starting values. */
export function fieldPrefill(data: WtFieldReadings | null): ReadingFormInitial | null {
  const p = data?.prefill;
  if (!p) return null;
  const entries: ReadingFormInitial['entries'] = {};
  for (const [k, v] of Object.entries(p.values ?? {})) entries[k] = { value: v.value, unit: v.unit };
  const tanks: ReadingFormInitial['tanks'] = {};
  for (const t of p.tanks ?? []) tanks[t.equipment_id] = { value: t.level, unit: t.level_unit };
  return { entries, tanks };
}

/** A minute clock, for the countdown. */
function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

export default function WtFieldReadingScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useWtFieldReadings(id);
  const jobQuery = useWtFieldJob(id);
  const catalog = useWtMeta().data?.data ?? null;
  const save = useSaveWtFieldReading();
  const [error, setError] = useState<string | null>(null);
  const now = useMinuteClock();

  const data = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const job = jobQuery.data?.data ?? null;
  const refused = wtNotOnCrewMessage(query) ?? wtNotOnCrewMessage(jobQuery);
  const fields = useMemo(() => fieldFormFields(data), [data]);
  const initial = useMemo(() => fieldPrefill(data), [data]);
  const tanks: ReadingFormTank[] = useMemo(
    () =>
      (job?.equipment ?? [])
        .filter((e) => e.component_kind === 'test_tank' && !e.off_date)
        .map((e) => ({ equipmentId: e.equipment_id, label: [e.equipment_name, e.unit_number].filter(Boolean).join(' · ') || 'Test tank' })),
    [job],
  );
  const tz = meta?.time_zone ?? null;
  const mins = wtMinutesUntil(data?.next_due_at, now);
  const overdue = mins !== null && mins < 0;
  const last = data?.readings?.[0] ?? null;

  return (
    <PtFieldPage title={job?.job_number ? `Reading · ${job.job_number}` : 'Enter reading'} backTo={id ? `/field/wt/jobs/${id}` : '/field/wt'} testId="wt-field-reading">
      {refused ? (
        <PtRefusal message={refused} testId="wt-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={query} subject="this job's readings">
          {!data ? (
            <PtRefusal message="That job no longer exists. It may have been deleted." />
          ) : !data.accepts_readings ? (
            <PtRefusal
              message={
                data.job_status === 'draft'
                  ? 'This job is still a draft. The office schedules it before readings can be entered.'
                  : 'This job is closed. It takes no more readings.'
              }
            />
          ) : jobQuery.isLoading ? (
            <p className="text-sm text-fieldiq-muted">Loading the job…</p>
          ) : (
            <div className="space-y-4">
              <PtFieldCard>
                <div className={`border-l-4 px-4 py-3 ${overdue ? 'border-l-fi-red' : 'border-l-fi-amber'}`} data-testid="wt-field-due">
                  <p className="text-base font-bold text-white">{wtDueText(mins)}</p>
                  <p className="mt-1 text-xs text-fieldiq-muted">
                    {last ? `Last reading ${formatInstant(last.read_at, tz) ?? '—'}` : 'No reading on this job yet.'}
                    {data.interval_min ? ` · every ${data.interval_min} min` : ''}
                  </p>
                </div>
              </PtFieldCard>
              {initial && <p className="text-xs text-fieldiq-muted">Filled in from the last reading. Change what moved, then save.</p>}
              <PtRefusal message={error} testId="wt-field-reading-refusal" />
              <ReadingForm
                key={data.prefill?.from_reading_id ?? 'first'}
                fields={fields}
                quantityUnits={catalog?.quantityUnits}
                tanks={tanks}
                tankDefaultUnit={meta?.unit_system === 'imperial' ? 'ft_in' : 'mm'}
                initial={initial}
                mode="new"
                variant="phone"
                pending={save.isPending}
                error={null}
                submitLabel="Save reading"
                testId="wt-field-reading-form"
                onSubmit={(body) => {
                  setError(null);
                  save.mutate(
                    { jobId: data.job_id, body },
                    {
                      onSuccess: () => {
                        toast.success('Reading saved');
                        navigate(`/field/wt/jobs/${data.job_id}`);
                      },
                      onError: (err) => setError(wtRefusal(err, 'The reading could not be saved.', WT_READING_REFUSALS)),
                    },
                  );
                }}
              />
            </div>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
